import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable, of, OperatorFunction, throwError } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { ConfigService, SentryLogger } from '@fcom/core/services';
import { DEFAULT_RETRY_BACKOFF_TIME, retryWithBackoff, STATUS_CODES_FOR_RETRY } from '@fcom/rx';
import { mapErrorForSentry } from '@fcom/core';
import {
  FinnairAdvancePassengerInformationRequest,
  FinnairEmergencyContactUpdateRequest,
  FinnairOrder,
  FinnairPassengerRequest,
  FinnairServiceRequestItem,
  TourOperatorCode,
} from '@fcom/dapi/api/models';
import { isNull } from '@fcom/utils';
import { DapiHttpResponse } from '@fcom/dapi/api/dapi-http-response';
import { AdvancePassengerInformationService, EmergencyContactService, OrderService } from '@fcom/dapi/api/services';
import { getDapiErrorKey } from '@fcom/common-booking/utils/common-booking.utils';

export const GET_ORDER_TIMEOUT_MS = 30000;

const ORDER_SUMMARY_STATUS_CODES_FOR_RETRY = [503, 403, 404, 400, ...STATUS_CODES_FOR_RETRY];

/**
 * OrderSummaryService is a wrapper around orderResourceV2Service which resolves the current locale
 * and will retry the fetch on failure.
 */
@Injectable()
export class OrderSummaryService {
  static NUMBER_OF_RETRIES = 1;
  static readonly LOCATION_HEADER = 'Location';

  private readonly CATCH_ERROR: (
    isTourOperator?: boolean,
    trackingCallback?: (errorKey: string) => void
  ) => OperatorFunction<unknown, unknown> = (isTourOperator = false) =>
    catchError((err: unknown) => {
      this.logErrorToSentry(err, getDapiErrorKey(err), isTourOperator);

      return throwError(() => {
        return err;
      });
    });

  logErrorToSentry(err: unknown, errorKey: string, isTourOperator = false): void {
    this.sentryLogger.error(`Error fetching order ${isTourOperator ? 'for tour operator ' : ''}${errorKey}`, {
      error: mapErrorForSentry(err),
    });
  }

  constructor(
    private http: HttpClient,
    private orderService: OrderService,
    private emergencyContactService: EmergencyContactService,
    private apisService: AdvancePassengerInformationService,
    private languageService: LanguageService,
    private configService: ConfigService,
    private sentryLogger: SentryLogger
  ) {}

  private locationFromResponse = (response: DapiHttpResponse<void>) =>
    response.headers.get(OrderSummaryService.LOCATION_HEADER);

  /**
   * load Order by PNR and surname
   *
   * @param familyName surname for the PNR
   * @param bookingRef bookingRef for the PNR to fetch
   */
  loadOrder(familyName: string, bookingRef: string, firstName?: string): Observable<FinnairOrder> {
    return this.orderService
      .getOrderByPnrAndLastName$Response(this.configService.cfg.orderUrl, {
        body: {
          id: bookingRef.toUpperCase(),
          lastName: familyName,
          locale: this.languageService.localeValue,
          ...(firstName ? { firstName } : {}),
        },
      })
      .pipe(
        timeout(GET_ORDER_TIMEOUT_MS),
        map((res) => this.locationFromResponse(res)),
        retryWithBackoff(
          OrderSummaryService.NUMBER_OF_RETRIES,
          DEFAULT_RETRY_BACKOFF_TIME,
          ORDER_SUMMARY_STATUS_CODES_FOR_RETRY
        ),
        this.CATCH_ERROR(),
        switchMap((orderLocation: string) => this.fetchOrder(orderLocation))
      );
  }

  /**
   * load Order by booking reference, surname and tour operator code
   *
   * @param familyName surname for the PNR
   * @param bookingRef bookingRef for the PNR to fetch
   * @param tourOperatorCode tourOperatorCode for the PNR to fetch
   */
  loadOrderTourOperator(
    familyName: string,
    bookingRef: string,
    tourOperatorCode: TourOperatorCode
  ): Observable<FinnairOrder> {
    return this.orderService
      .getOrderByTourOperatorAndLastName$Response(this.configService.cfg.orderUrl, {
        body: {
          id: bookingRef.toUpperCase(),
          lastName: familyName,
          locale: this.languageService.localeValue,
          tourOperatorCode,
        },
      })
      .pipe(
        timeout(GET_ORDER_TIMEOUT_MS),
        map((res) => this.locationFromResponse(res)),
        retryWithBackoff(
          OrderSummaryService.NUMBER_OF_RETRIES,
          DEFAULT_RETRY_BACKOFF_TIME,
          ORDER_SUMMARY_STATUS_CODES_FOR_RETRY
        ),
        this.CATCH_ERROR(true),
        switchMap((orderLocation: string) => this.fetchOrder(orderLocation))
      );
  }

  setServices(orderId: string, hash: string, serviceRequests: FinnairServiceRequestItem[]): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.orderService.setServices$Response(this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequests,
      })
    );
  }

  updateEmergencyContactDetails(
    orderId: string,
    hash: string,
    serviceRequest: FinnairEmergencyContactUpdateRequest
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.emergencyContactService.updateEmergencyContactDetails$Response(this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  updateApis(
    orderId: string,
    hash: string,
    serviceRequest: FinnairAdvancePassengerInformationRequest[]
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.apisService.updateAdvancePassengerInformation$Response(this.configService.cfg.orderUrl, {
        orderId: orderId,
        hash: hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  updateContactDetails(
    orderId: string,
    hash: string,
    serviceRequest: FinnairPassengerRequest
  ): Observable<FinnairOrder> {
    return this.fetchOrderAfterRequest(
      this.orderService.updatePassengerDetails$Response(this.configService.cfg.orderUrl, {
        orderId,
        hash,
        locale: this.languageService.localeValue,
        body: serviceRequest,
      })
    );
  }

  private fetchOrderAfterRequest(request$: Observable<DapiHttpResponse<void>>): Observable<FinnairOrder> {
    return request$.pipe(
      timeout(GET_ORDER_TIMEOUT_MS),
      map((res) => this.locationFromResponse(res)),
      retryWithBackoff(OrderSummaryService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, [
        503,
        ...STATUS_CODES_FOR_RETRY,
      ]),
      switchMap((location: string) => this.fetchOrder(location))
    );
  }

  public fetchOrder(orderUrl: string | null): Observable<FinnairOrder> {
    if (isNull(orderUrl)) {
      return of(null);
    }

    const params = new HttpParams().set('includeServiceCatalog', true);

    return this.http.get<FinnairOrder>(orderUrl, { params }).pipe(
      retryWithBackoff(OrderSummaryService.NUMBER_OF_RETRIES, DEFAULT_RETRY_BACKOFF_TIME, [
        503,
        ...STATUS_CODES_FOR_RETRY,
      ]),
      catchError((err: unknown) => {
        this.logErrorToSentry(err, getDapiErrorKey(err));
        return throwError(() => err);
      })
    );
  }
}
